import FileSaver from "file-saver";
import { CachedItem } from "@lib-shared/lib";
import Messages from "core/constants/messages";
import { logApiError } from "core/lib";
import { dateWrapper } from "core/lib/date.wrapper";
import UserActivityLoggingApi, { UserActivityLog } from "core/services/user-activity-logging.service";
import UserActivityLogStore from "./user-activity-logger.store";

export const direction = {
    asc: "asc",
    desc: "desc",
    toggle(dir: string) {
        return dir === this.asc ? this.desc : this.asc;
    }
}

export const fields = {
    dateTime: "datetime",
    name: "name",
    userType: "usertype",
    action: "action"
}

const secondarySort = {
    "datetime": null,
    "name": fields.dateTime,
    "usertype": fields.name,
    "action": fields.name
}

const activityUsers = new CachedItem("activityUsers");

const compareStrings = (a: string, b: string) => a.toLowerCase().localeCompare(b.toLowerCase());

export const logName = (log: UserActivityLog) => {
    const name = `${log.firstName} ${log.lastName}`.split("null").join(" ").trim();
    return name.length === 0
        ? log.email.length === 0
            ? log.id : log.email
        : name;
}

const compare = (field: string, a: UserActivityLog, b: UserActivityLog): number => {
    const key = field as keyof typeof secondarySort;
    const field2 = secondarySort[key];

    switch (field) {
        case fields.dateTime:
            if (a.dateCreated < b.dateCreated) {
                return -1;
            }

            if (a.dateCreated > b.dateCreated) {
                return 1;
            }

            if (field2 != null) {
                return compare(field2, a, b);
            }

            return 0;

        case fields.action:
            return compareStrings(a.description, b.description);

        case fields.name:
            return compareStrings(logName(a), logName(b));

        case fields.userType:
            return compareStrings(a.userType, b.userType);

        default:
            return 0;
    }
}

const getDateString = (date: Date | string) => dateWrapper(date).yearMonthDay();

export class UserActivityLogStoreManager {

    _api = new UserActivityLoggingApi();
    _onError: any;
    _onUsersError: any;
    _store: UserActivityLogStore;

    constructor(onError: any, onUsersError: any, store: UserActivityLogStore) {
        this._onError = onError;
        this._onUsersError = onUsersError;
        this._store = store;
    }

    async getUsersAsync() {
        if (activityUsers.isCached) {
            this._store.updateUsers(activityUsers.value);
            return;
        }

        try {
            const response = await this._api.getUsers();

            activityUsers.set(response.data);

            this._store.updateUsers(response.data);
        } catch (error) {
            logApiError(error);

            this._onUsersError(error);

            this._store.updateUsers([]);
        }
    }

    getRequest(state: any) {
        let userId = state?.ciamId;
        userId = userId === "" ? null : userId;

        const request = {
            dateRange: {
                start: getDateString(state?.startDate),
                end: getDateString(state?.endDate) + " 23:59:59"
            },
            ciamId: userId,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };

        return request;
    }

    async getLogsAsync(state: any) {
        const request = this.getRequest(state);

        try {
            const response = await this._api.filterLogsAsync(request);
            return this._processResponse(response);
        } catch (error) {
            logApiError(error);

            this._handleErrorGettingItems(error);
        }
    }

    sortLogs(logs: UserActivityLog[], field: string, reverse = false) {
        const sortFunction = (a: UserActivityLog, b: UserActivityLog) => compare(field, a, b);

        logs.sort(sortFunction);

        if (reverse) {
            logs.reverse();
        }

        return logs;
    }

    async downloadAsync(fileName: string, state: any) {
        const request = this.getRequest(state);

        const response = await this._api.downloadLogsAsync(request);

        if (response.status !== 200) {
            return;
        }

        const blob = new Blob([response.data], { type: response.headers["content-type"] });

        FileSaver.saveAs(blob, fileName);
    }

    _processResponse(response: any) {
        if (response.status !== 200) {
            this._handleErrorGettingItems();
        }

        const data = response.data;

        let error = data?.exception || data?.errorDescription;

        if (error?.includes("System.")) {
            error = Messages.apiError;
        }

        if (error != null) {
            console.log(data);
            this._onError(error);
            return [];
        }

        const newItems = data || [];

        this._enhanceData(newItems);

        this._store.updateItems(newItems);

        return newItems
    }

    _enhanceData(data: UserActivityLog[]) {
        data.forEach(x => {
            const user = this._store.users.find(u => u.id === x.id);
            x.firstName = user?.firstName ?? "";
            x.lastName = user?.lastName ?? "";
            x.email = user?.name ?? "";
            x.userType = user?.userType ?? "";

            const date = dateWrapper(x.dateCreated);
            x.date = date.shortDate;
            x.time = date.time;
        });
    }

    _handleErrorGettingItems(error: any = null) {
        if (error === null) {
            return;
        }

        this._onError(error);
    }
}

export default { direction, UserActivityLogStoreManager, fields, logName }
